import React from 'react';
import Button from '@material-ui/core/Button';
import { makeStyles, type Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import DescriptionIcon from '@material-ui/icons/Description';
import Skeleton from '@material-ui/lab/Skeleton';
import { colors } from '@skyslope/mache';
import { Documents } from '../../../common/documents';
import type { IDoc } from '../../../common/documents/types';
import { progressPercentageForDocuments } from '../../../lib/api/common/progressPercentage';
import { chameleonElId } from '../../../lib/constants';
import { useShallowSelector, useDispatch } from '../../../lib/reduxHooks';
import { hex2rgba } from '../../../lib/utils';
import type { IRootState } from '../../../store';
import * as actions from '../../../store/dtm/documents/actions';
import type { IDocument } from '../../../store/dtm/documents/types';
import { errorToast } from '../../../store/pageFrame/actions';
import Section from './Section';
import SectionHeader from './SectionHeader';

const useStyles = makeStyles((theme: Theme) => ({
  focusButton: {
    '&:focus': {
      backgroundColor: colors.blue[50],
    },
  },
  descriptionIcon: {
    color: colors.grey[500],
    marginRight: theme.spacing(1),
  },
  documentAddIcon: {
    height: 20,
    width: 20,
    margin: '-4px 4px -4px -4px',
  },
  placeholder: {
    padding: '32px 32px 24px',
    border: `2px dashed ${colors.grey[400]}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    alignSelf: 'stretch',
    marginBottom: 24,
    position: 'relative',
  },
  placeholderOverlay: {
    position: 'absolute',
    backgroundColor: hex2rgba(colors.blue[50], 0.9),
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    fontSize: 160,
    color: 'white',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2,
    userSelect: 'none',
  },
  placeholderText: {
    textAlign: 'center',
    color: '#6788A2',
    width: '100%',
  },
  envelopeSection: {
    padding: '32px 24px',
    flexGrow: 1,
    display: 'flex',
    alignItems: 'flex-start',
    overflowY: 'auto',
  },
}));

interface IState {
  envelopeDocuments: IDocument[];
  isFetching: boolean;
  envelopeDocumentsFetchComplete: boolean;
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const selector = (state: IRootState) => ({
  envelopeDocuments: state.dtm.documents.envelopeDocuments,
  isFetching: state.dtm.documents.isFetching,
  envelopeDocumentsFetchComplete: state.dtm.documents.envelopeDocumentsFetchComplete,
});

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const EnvelopeSection = () => {
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const store: IState = useShallowSelector(selector);
  const dispatch = useDispatch();
  const classes = useStyles();

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleUploadDocument = (tempId: string, file: File) => {
    dispatch(actions.uploadDocument(tempId, file));
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleReorderDocuments = (documents: IDocument[]) => {
    dispatch(actions.reorderDocuments(documents));
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleDeleteDocument = (documentId: string) => {
    dispatch(actions.deleteDocument(documentId));
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleUpdateDocuments = (documents: IDocument[]) => {
    dispatch(actions.updateDocuments(documents));
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleError = (errStr: string) => {
    dispatch(errorToast(errStr));
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const documentsHeader = (isDragActive: boolean, open: () => void) => (
    <div className={classes.placeholder} id={chameleonElId(10)}>
      {isDragActive && <div className={classes.placeholderOverlay}>+</div>}
      <Typography variant="body1" className={classes.placeholderText}>
        Drag and drop .pdf, .doc, .docx, or .docm documents here
        <br />
        (Maximum size 25MB) to upload or
      </Typography>
      <Button color="primary" className={classes.focusButton} onClick={() => open()}>
        <AddIcon className={classes.documentAddIcon} />
        Upload Document
      </Button>
    </div>
  );

  const documentsStr =
    store.isFetching && !store.envelopeDocumentsFetchComplete ? (
      <Skeleton animation="wave" />
    ) : (
      `${store.envelopeDocuments.length} DOCUMENT${store.envelopeDocuments.length === 1 ? '' : 'S'} ADDED`
    );

  return (
    <Section id={chameleonElId(16)}>
      <SectionHeader>
        <DescriptionIcon className={classes.descriptionIcon} />
        <Typography variant="subtitle1" style={{ width: store.isFetching ? '100%' : '' }}>
          {documentsStr}
        </Typography>
      </SectionHeader>
      <div className={classes.envelopeSection}>
        {/* @ts-expect-error -- This code was written prior to enforcing lint rules */}
        <Documents
          documents={store.envelopeDocuments as IDoc[]}
          handleUploadDocument={handleUploadDocument}
          handleReorderDocuments={handleReorderDocuments}
          handleDeleteDocument={handleDeleteDocument}
          handleUpdateDocuments={handleUpdateDocuments}
          handleError={handleError}
          header={({ isDragActive, open }) => documentsHeader(isDragActive, open)}
          footer={() => ''}
          progressPercentageForDocuments={progressPercentageForDocuments}
          inlineRemoveButton
          smallerPadding
          isFetching={store.isFetching}
          envelopeDocumentsFetchComplete={store.envelopeDocumentsFetchComplete}
        />
      </div>
    </Section>
  );
};

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default EnvelopeSection;
